<script setup>
import { storeToRefs } from 'pinia'
import Locator from 'assets/icons/locator.svg'
import Home from 'assets/icons/home.svg'
import { useSideBarStore } from '~/stores/sideBar'
import { useMessageStore } from '~/stores/messages'

defineProps({
    centerSelectorConfig: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                position: rawValue => rawValue?.position === undefined || (typeof rawValue?.position === 'string' && ['header', 'content', 'navigation'].includes(rawValue.position))
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch a center list ito check if center route is valid
const { data: centers, suspense: centerSuspense } = useFetchCenterList()

onServerPrefetch(async () => {
    await centerSuspense()
})

const isValidCenterRoute = computed(() => centers.value?.some(item => item.uid === centerRoute.value))

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = isValidCenterRoute.value ? useFetchCenterDetails(centerRoute.value) : {}

onServerPrefetch(async () => {
    if (isValidCenterRoute.value) {
        await centerDetailSuspense()
    }
})

// Get sideBar status
const { sideBarContent, sideBarOverlayContent } = storeToRefs(useSideBarStore())

// Get label
const getCenterSelectorLabel = computed(() => isValidCenterRoute.value ? centerDetails.value?.centerName : staticKeys.value?.center_selector_label)

// Check if center selector is open
const centerSelectorIsOpen = computed(() => {
    return (sideBarContent.value?.open && sideBarContent.value?.content === 'centerSelector') || (sideBarOverlayContent.value?.open && sideBarOverlayContent.value?.content === 'centerDetail')
})

// Get header center selector class
const getHeaderCenterSelectorClass = computed(() => {
    return {
        'header-center-selector-active': centerSelectorIsOpen.value,
    }
})

// Emit close center navigation
const emit = defineEmits(['update:closeCenterNavigation'])

/**
 *  Open center selector, depending on center name
 * @returns {Promise<void>}
 */
const openCenterSelector = async () => {
    const query = isValidCenterRoute.value ? { 'center-details': null } : { 'center-switch': null }

    await navigateWithSidebarQuery({
        query,
        route
    })
}

/**
 *  Open center selector  and closes the center navigation
 *  @returns {Promise<void>}
 */
const handleCenterSelectorClick = async () => {
    await openCenterSelector()
    emit('update:closeCenterNavigation')
}
</script>

<template>
    <ShadowContentBox
        v-if="centerSelectorConfig.position === 'content'"
        :shadow-content-box-config="{ type: 'div' }"
        class="center-selector-wrapper"
        :title="staticKeys?.center_selector_label"
        @click="openCenterSelector"
    >
        <div class="center-selector-container">
            <div class="center-selector-label">
                <Locator />
                <span>
                    {{ getCenterSelectorLabel }}
                </span>
            </div>
        </div>
    </ShadowContentBox>

    <div
        v-if="centerSelectorConfig.position === 'header'"
        class="header-center-selector"
        :class="getHeaderCenterSelectorClass"
        :title="staticKeys?.center_selector_label"
        @click="openCenterSelector"
    >
        <Home />
    </div>

    <div
        v-if="centerSelectorConfig.position === 'navigation'"
        class="page-navigation-center-selector btn-active"
        :title="staticKeys?.center_selector_label"
        @click="handleCenterSelectorClick"
    >
        <Home />
        <span>
            {{ getCenterSelectorLabel }}
        </span>
    </div>
</template>

<style lang="scss">
.center-selector-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-flow: row wrap;

    @include body2-extrabold;

    > svg {
        width: 14px;
        height: 28px;
        margin-inline-end: 40px;
    }

    span {
        overflow: hidden;
        display: block;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: calc(100% - 54px);
    }
}

.page-navigation-center-selector {
    pointer-events: all;
    border: $page-navigation-center-selector-border 1px solid;
    color: $page-navigation-center-selector;
    cursor: pointer;
    z-index: z('page-navigation-center-selector');
    white-space: nowrap;
    border-radius: 100px;
    padding-block: 0;
    padding-inline: 40px;
    min-height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    @include body2-medium;

    > svg {
        fill: currentcolor;
        width: 20px;
        height: 20px;
        margin-inline-end: px2rem(10);
    }
}

.header-center-selector {
    grid-column: 6/span 1;
    grid-row: 1;
    cursor: pointer;
    height: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    @include bp(medium1) {
        grid-column: 8/span 1;
    }

    @include bp(large1) {
        grid-column: 12/span 1;
        width: 100%;
    }

    > svg {
        fill: $header-center-selector-bright;
        width: 20px;

        @include bp(medium1) {
            fill: $header-center-selector;
        }
    }

    &.is-center-page {
        grid-column: 5/span 1;
        margin-inline-end: 50px;

        @include bp(medium1) {
            grid-column: 8/span 1;
            margin-inline-end: 54px;
        }

        @include bp(large1) {
            grid-column: 12/span 1;
            margin-inline-end: 0;
        }
    }

    @media (hover: hover) {
        &:hover,
        &.header-center-selector-active {
            > svg {
                fill: $header-center-selector-active;
                transition: fill 400ms ease-in-out;
            }
        }
    }
}

.center-selector-wrapper {
    height: 80px;
    padding: 0 20px;
    display: flex;
    align-items: center;

    .center-header & {
        width: auto;
        min-width: 360px;
    }
}

.center-selector-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    width: 100%;
}
</style>
